import React, { useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon } from 'lucide-react';
import { motivations } from '../data/motivations';
import { formatEuro } from '../utils/currency';
import type { PriceTag } from '../types/content';
const tagPosition: Record<PriceTag['position'], string> = {
  'top-left': 'left-1/4 top-[36%]',
  'top-right': 'left-3/4 top-[36%]',
  'bottom-left': 'left-1/4 bottom-[4%]',
  'bottom-right': 'left-3/4 bottom-[4%]'
};
export function Motivations() {
  const [activeIndex, setActiveIndex] = useState(0);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const active = motivations[activeIndex];
  const focusTab = (index: number) => {
    const next = (index + motivations.length) % motivations.length;
    setActiveIndex(next);
    tabRefs.current[next]?.focus();
  };
  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowRight') focusTab(activeIndex + 1);else if (e.key === 'ArrowLeft') focusTab(activeIndex - 1);else if (e.key === 'Home') focusTab(0);else if (e.key === 'End') focusTab(motivations.length - 1);else return;
    e.preventDefault();
  };
  return <section aria-labelledby="motivations-title" className="bg-sand px-5 sm:px-8 py-16 sm:py-24" data-id="element-148" data-mp="1h7b3lw9fom2k">
      <div className="max-w-6xl mx-auto" data-id="element-149" data-mp="1d69218s1nxxf">
        <h2 id="motivations-title" className="font-serif text-3xl sm:text-[2.6rem] leading-tight font-semibold text-teal max-w-xl" data-id="element-150" data-mp="1d7aosaifa8uz">
          La creatività, a modo tuo
        </h2>

        <div role="tablist" aria-label="Perché creare" className="mt-8 -mx-5 px-5 sm:mx-0 sm:px-0 flex gap-2 overflow-x-auto no-scrollbar" data-id="element-151" data-mp="24wvr8sgj87i6">
          {motivations.map((m, i) => {
          const selected = i === activeIndex;
          return <button key={m.id} ref={el => {
            tabRefs.current[i] = el;
          }} role="tab" id={`tab-${m.id}`} aria-selected={selected} aria-controls={`panel-${m.id}`} tabIndex={selected ? 0 : -1} onClick={() => setActiveIndex(i)} onKeyDown={onKeyDown} className={`min-h-[44px] shrink-0 whitespace-nowrap rounded-full px-5 py-2.5 text-[15px] font-semibold transition-colors duration-200 ${selected ? 'bg-teal text-ivory' : 'bg-ivory text-ink hover:bg-white'}`} data-id="element-152" data-mp="1w4ldj16fhjw6" data-mpi={i}>
                {m.label}
              </button>;
        })}
        </div>

        <div role="tabpanel" id={`panel-${active.id}`} aria-labelledby={`tab-${active.id}`} className="mt-8 rounded-[2rem] bg-ivory overflow-hidden shadow-soft" data-id="element-153" data-mp="h78zihfz1snw">
          <AnimatePresence mode="wait" initial={false} data-id="element-154" data-mp="3a2lt1a3uk5j">
            <motion.div key={active.id} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.18,
            ease: [0.23, 1, 0.32, 1]
          }} className="grid md:grid-cols-2" data-id="element-155" data-mp="8840zb4etzbq">
              {active.priceTags ? <div className="bg-sand p-4 sm:p-6 flex flex-col justify-center" data-id="element-156" data-mp="1nome72ikwp8l">
                  {active.priceTagsTitle && <p className="mb-3 font-serif text-lg font-semibold text-teal" data-id="element-157" data-mp="1sktmh1yg6ctp">
                      {active.priceTagsTitle}
                    </p>}
                  <div className="relative aspect-square w-full overflow-hidden rounded-2xl" data-id="element-158" data-mp="386n6l5b7g43j">
                    <img src={active.image} alt={active.imageAlt} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" data-id="element-159" data-mp="3mv1hm3iryyv6" />
                    {active.priceTags.map(tag => <span key={tag.label} className={`absolute -translate-x-1/2 flex flex-col items-center rounded-xl bg-white/95 px-2.5 py-1.5 text-center shadow-soft ${tagPosition[tag.position]}`} data-id="element-160" data-mp="3vv6e3imfkcuk">
                        <span className="text-xs sm:text-sm font-medium text-ink whitespace-nowrap" data-id="element-161" data-mp="9orno4kmi1rr">{tag.label}</span>
                        <span className="font-serif text-base sm:text-lg font-semibold leading-tight text-coral-deep whitespace-nowrap" data-id="element-162" data-mp="4ovfdt4w7o6e">
                          {formatEuro(tag.amount)}
                        </span>
                      </span>)}
                  </div>
                </div> : <div className="relative aspect-[4/5] sm:aspect-[4/3] md:aspect-auto md:min-h-[480px] bg-sand" data-id="element-163" data-mp="27xxg3akj4axk">
                  <img src={active.image} alt={active.imageAlt} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" data-id="element-164" data-mp="2cvz1i25fgmc7" />
                </div>}
              <div className="p-7 sm:p-10 lg:p-14 flex flex-col justify-center" data-id="element-165" data-mp="2muxxol90apib">
                <h3 className="font-serif text-2xl sm:text-3xl leading-snug font-semibold text-teal" data-id="element-166" data-mp="2msv8u4jlb2f6">
                  {active.title}
                </h3>
                <p className="mt-4 text-base leading-relaxed text-ink/80" data-id="element-167" data-mp="1u7bwtkr5xyz5">{active.description}</p>
                <ul className="mt-7 space-y-3.5" data-id="element-168" data-mp="1z4c2vvo0j1q9">
                  {active.benefits.map(b => <li key={b} className="flex items-start gap-3 text-base text-ink" data-id="element-169" data-mp="23wlq33xqtnou">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-teal-soft text-teal" data-id="element-170" data-mp="16gy21mh71m0v">
                        <CheckIcon size={12} strokeWidth={3} aria-hidden="true" data-id="element-171" data-mp="1berakm5yys4b" />
                      </span>
                      {b}
                    </li>)}
                </ul>
                {active.note && <p className="mt-8 pt-5 border-t border-ink/10 text-sm leading-relaxed text-ink/70" data-id="element-172" data-mp="3kpijvtr26kgt">
                    {active.note}
                  </p>}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>;
}